// Rating service - handles user ratings and reviews
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc,
  setDoc, 
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  increment,
  deleteField
} from 'firebase/firestore';

/**
 * Normalize wallet address
 */
const normalize = (addr) => {
  if (!addr || typeof addr !== 'string') {
    throw new Error('Invalid address: must be a non-empty string');
  }
  let normalized = addr.toLowerCase().trim();
  if (!normalized.startsWith('0x')) {
    normalized = '0x' + normalized;
  }
  return normalized;
};

/**
 * Submit a rating/review for a user
 * @param {string} reviewerAddress - Wallet address of the user leaving the review
 * @param {string} reviewedUserAddress - Wallet address of the user being reviewed
 * @param {number} rating - Rating from 1 to 5
 * @param {string} reviewText - Optional review text
 * @returns {Promise<string>} Review ID
 */
export const submitReview = async (reviewerAddress, reviewedUserAddress, rating, reviewText = '') => {
  if (!reviewerAddress || !reviewedUserAddress) {
    throw new Error('Reviewer and reviewed user addresses are required');
  }

  if (rating < 1 || rating > 5 || !Number.isInteger(rating)) {
    throw new Error('Rating must be an integer between 1 and 5');
  }

  const reviewer = normalize(reviewerAddress);
  const reviewed = normalize(reviewedUserAddress);

  // Prevent self-review
  if (reviewer === reviewed) {
    throw new Error('Cannot review yourself');
  }

  const reviewId = `review_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  let existingReviewId = null;

  try {
    // Check if reviewer already reviewed this user (BEFORE transaction - queries not allowed in transactions)
    const existingReviewQuery = query(
      collection(db, 'reviews'),
      where('reviewerAddress', '==', reviewer),
      where('reviewedUserAddress', '==', reviewed)
    );
    const existingReviews = await getDocs(existingReviewQuery);
    
    let oldRating = null;
    existingReviews.forEach((doc) => {
      existingReviewId = doc.id;
      oldRating = doc.data().rating;
    });

    // Use transaction to ensure atomicity for writes only
    await runTransaction(db, async (transaction) => {
      // STEP 1: ALL READS FIRST (Firestore requirement)
      // Read review document if it exists (for update case)
      let reviewDoc = null;
      if (existingReviewId) {
        const reviewRef = doc(db, 'reviews', existingReviewId);
        reviewDoc = await transaction.get(reviewRef);
      }

      // Read rating summary document
      const ratingSummaryRef = doc(db, 'userRatings', reviewed);
      const ratingSummaryDoc = await transaction.get(ratingSummaryRef);

      // STEP 2: ALL WRITES AFTER READS
      // Create or update review document
      const reviewRef = doc(db, 'reviews', existingReviewId || reviewId);
      
      // Build review data - for updates, omit createdAt; for creates, include it
      const reviewData = {
        reviewId: existingReviewId || reviewId,
        reviewerAddress: reviewer,
        reviewedUserAddress: reviewed,
        rating: rating,
        reviewText: reviewText.trim(),
        updatedAt: serverTimestamp(),
        timestamp: Date.now()
      };
      
      // Only add createdAt for new reviews (not updates)
      if (!existingReviewId) {
        reviewData.createdAt = serverTimestamp();
      }

      if (existingReviewId) {
        transaction.update(reviewRef, reviewData);
      } else {
        transaction.set(reviewRef, reviewData);
      }

      if (ratingSummaryDoc.exists()) {
        const currentData = ratingSummaryDoc.data();
        const currentTotalRating = currentData.totalRating || 0;
        const currentReviewCount = currentData.reviewCount || 0;
        
        let newTotalRating = currentTotalRating;
        let newReviewCount = currentReviewCount;

        if (existingReviewId && oldRating !== null) {
          // Update existing review: subtract old rating, add new rating
          newTotalRating = currentTotalRating - oldRating + rating;
        } else {
          // New review: add rating and increment count
          newTotalRating = currentTotalRating + rating;
          newReviewCount = currentReviewCount + 1;
        }

        const averageRating = newReviewCount > 0 ? newTotalRating / newReviewCount : 0;

        transaction.update(ratingSummaryRef, {
          totalRating: newTotalRating,
          reviewCount: newReviewCount,
          averageRating: averageRating,
          updatedAt: serverTimestamp()
        });
      } else {
        // First review for this user
        transaction.set(ratingSummaryRef, {
          totalRating: rating,
          reviewCount: 1,
          averageRating: rating,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    });

    return existingReviewId || reviewId;
  } catch (error) {
    console.error('Error submitting review:', error);
    throw error;
  }
};

/**
 * Get rating summary for a user
 * @param {string} userAddress - Wallet address
 * @returns {Promise<Object|null>} Rating summary with averageRating, reviewCount, totalRating
 */
export const getUserRatingSummary = async (userAddress) => {
  if (!userAddress) return null;

  try {
    const user = normalize(userAddress);
    const ratingSummaryRef = doc(db, 'userRatings', user);
    const ratingSummaryDoc = await getDoc(ratingSummaryRef);
    
    if (ratingSummaryDoc.exists()) {
      const data = ratingSummaryDoc.data();
      return {
        averageRating: data.averageRating || 0,
        reviewCount: data.reviewCount || 0,
        totalRating: data.totalRating || 0
      };
    }
    
    // Return default values for users with no ratings
    return {
      averageRating: 0,
      reviewCount: 0,
      totalRating: 0
    };
  } catch (error) {
    console.error('Error getting user rating summary:', error);
    return {
      averageRating: 0,
      reviewCount: 0,
      totalRating: 0
    };
  }
};

/**
 * Get all reviews for a user
 * @param {string} userAddress - Wallet address of the reviewed user
 * @param {Object} options - Options
 * @param {number} options.limitCount - Maximum number of reviews to retrieve
 * @returns {Promise<Array>} Array of reviews
 */
export const getUserReviews = async (userAddress, options = {}) => {
  if (!userAddress) return [];

  const { limitCount = 50 } = options;

  try {
    const user = normalize(userAddress);
    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('reviewedUserAddress', '==', user),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const reviews = [];

    querySnapshot.forEach((doc) => {
      const reviewData = doc.data();
      reviews.push({
        id: doc.id,
        ...reviewData
      });
    });

    return reviews;
  } catch (error) {
    console.error('Error getting user reviews:', error);
    // If orderBy fails (missing index), try without it
    try {
      const user = normalize(userAddress);
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef,
        where('reviewedUserAddress', '==', user),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const reviews = [];

      querySnapshot.forEach((doc) => {
        const reviewData = doc.data();
        reviews.push({
          id: doc.id,
          ...reviewData
        });
      });

      // Sort client-side
      reviews.sort((a, b) => {
        const aTime = a.timestamp || (a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0);
        const bTime = b.timestamp || (b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0);
        return bTime - aTime;
      });

      return reviews;
    } catch (fallbackError) {
      console.error('Error getting user reviews (fallback):', fallbackError);
      return [];
    }
  }
};

/**
 * Get review by reviewer for a specific user
 * @param {string} reviewerAddress - Wallet address of the reviewer
 * @param {string} reviewedUserAddress - Wallet address of the reviewed user
 * @returns {Promise<Object|null>} Review object or null
 */
export const getReviewByReviewer = async (reviewerAddress, reviewedUserAddress) => {
  if (!reviewerAddress || !reviewedUserAddress) return null;

  try {
    const reviewer = normalize(reviewerAddress);
    const reviewed = normalize(reviewedUserAddress);

    const reviewsRef = collection(db, 'reviews');
    const q = query(
      reviewsRef,
      where('reviewerAddress', '==', reviewer),
      where('reviewedUserAddress', '==', reviewed),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting review by reviewer:', error);
    return null;
  }
};

/**
 * Subscribe to rating summary changes
 * @param {string} userAddress - Wallet address
 * @param {Function} callback - Callback function that receives rating summary
 * @returns {Function} Unsubscribe function
 */
export const subscribeToRatingSummary = (userAddress, callback) => {
  if (!userAddress) {
    callback({
      averageRating: 0,
      reviewCount: 0,
      totalRating: 0
    });
    return () => {};
  }

  try {
    const user = normalize(userAddress);
    const ratingSummaryRef = doc(db, 'userRatings', user);

    const unsubscribe = onSnapshot(
      ratingSummaryRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          callback({
            averageRating: data.averageRating || 0,
            reviewCount: data.reviewCount || 0,
            totalRating: data.totalRating || 0
          });
        } else {
          callback({
            averageRating: 0,
            reviewCount: 0,
            totalRating: 0
          });
        }
      },
      (error) => {
        console.error('Error subscribing to rating summary:', error);
        callback({
          averageRating: 0,
          reviewCount: 0,
          totalRating: 0
        });
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up rating summary subscription:', error);
    callback({
      averageRating: 0,
      reviewCount: 0,
      totalRating: 0
    });
    return () => {};
  }
};

/**
 * Subscribe to reviews for a user
 * @param {string} userAddress - Wallet address of the reviewed user
 * @param {Function} callback - Callback function that receives array of reviews
 * @param {Object} options - Options
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserReviews = (userAddress, callback, options = {}) => {
  if (!userAddress) {
    callback([]);
    return () => {};
  }

  const { limitCount = 50 } = options;

  try {
    const user = normalize(userAddress);
    const reviewsRef = collection(db, 'reviews');
    let q = query(
      reviewsRef,
      where('reviewedUserAddress', '==', user)
    );

    // Try with orderBy, but handle errors if index doesn't exist
    try {
      q = query(q, orderBy('timestamp', 'desc'), limit(limitCount));
    } catch (orderByError) {
      console.warn('orderBy not available, using limit only:', orderByError);
      q = query(q, limit(limitCount));
    }

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const reviews = [];
        querySnapshot.forEach((doc) => {
          const reviewData = doc.data();
          reviews.push({
            id: doc.id,
            ...reviewData
          });
        });

        // Sort client-side if orderBy wasn't available
        reviews.sort((a, b) => {
          const aTime = a.timestamp || (a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0);
          const bTime = b.timestamp || (b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0);
          return bTime - aTime;
        });

        callback(reviews.slice(0, limitCount));
      },
      (error) => {
        console.error('Error subscribing to user reviews:', error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up user reviews subscription:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Delete a review
 * @param {string} reviewId - Review ID to delete
 * @param {string} reviewerAddress - Wallet address of the reviewer (must match to delete)
 * @returns {Promise<void>}
 */
export const deleteReview = async (reviewId, reviewerAddress) => {
  if (!reviewId || !reviewerAddress) {
    throw new Error('Review ID and reviewer address are required');
  }

  const reviewer = normalize(reviewerAddress);

  try {
    // First, get the review to verify ownership and get the reviewed user address
    const reviewRef = doc(db, 'reviews', reviewId);
    const reviewDoc = await getDoc(reviewRef);
    
    if (!reviewDoc.exists()) {
      throw new Error('Review not found');
    }

    const reviewData = reviewDoc.data();
    
    // Verify that the reviewer address matches
    if (reviewData.reviewerAddress.toLowerCase() !== reviewer.toLowerCase()) {
      throw new Error('You can only delete your own reviews');
    }

    const reviewedUserAddress = reviewData.reviewedUserAddress;
    const rating = reviewData.rating || 0;

    // Use transaction to ensure atomicity
    await runTransaction(db, async (transaction) => {
      // STEP 1: ALL READS FIRST
      // Read rating summary document
      const ratingSummaryRef = doc(db, 'userRatings', reviewedUserAddress);
      const ratingSummaryDoc = await transaction.get(ratingSummaryRef);

      // STEP 2: ALL WRITES AFTER READS
      // Delete the review document
      transaction.delete(reviewRef);

      // Update rating summary
      if (ratingSummaryDoc.exists()) {
        const currentData = ratingSummaryDoc.data();
        const currentTotalRating = currentData.totalRating || 0;
        const currentReviewCount = currentData.reviewCount || 0;

        // Subtract the rating and decrement count
        const newTotalRating = Math.max(0, currentTotalRating - rating);
        const newReviewCount = Math.max(0, currentReviewCount - 1);
        const averageRating = newReviewCount > 0 ? newTotalRating / newReviewCount : 0;

        if (newReviewCount === 0) {
          // If no reviews left, delete the rating summary document
          transaction.delete(ratingSummaryRef);
        } else {
          transaction.update(ratingSummaryRef, {
            totalRating: newTotalRating,
            reviewCount: newReviewCount,
            averageRating: averageRating,
            updatedAt: serverTimestamp()
          });
        }
      }
    });
  } catch (error) {
    console.error('Error deleting review:', error);
    throw error;
  }
};

